import {
  ColumnsPicker,
  ExportButton,
  i18nKeyForError,
  isSessionError,
  NoIdentityState,
  PerPageSelect,
  parseApiError,
  usePaginationLabels,
} from '@features/allocation';
import {
  Alert,
  Button,
  EmptyState,
  PageHeader,
  Pagination,
  Skeleton,
} from '@ntconsult/aluma-react';
import { Plus, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ALLOCATION_TAG_COLUMN_LABEL_KEYS,
  useAllocationTagColumns,
} from '../hooks/useAllocationTagColumns';
import { useAllocationTagFilters } from '../hooks/useAllocationTagFilters';
import { useAllocationTags } from '../hooks/useAllocationTags';
import { useExportAllocationTags } from '../hooks/useExportAllocationTags';
import type { AllocationTag } from '../types/AllocationTag';
import { AllocationTagFilterChips } from './AllocationTagFilterChips';
import { AllocationTagFiltersDrawer } from './AllocationTagFiltersDrawer';
import { AllocationTagModal } from './AllocationTagModal';
import { AllocationTagsTable } from './AllocationTagsTable';
import { DeleteAllocationTagDialog } from './DeleteAllocationTagDialog';

/** Cadastro de `alocacao_tag`, como o `AlocacaoTagHeaderList.php` do legado. Só admin (rota e API). */
export function AllocationTagsPage() {
  const { t } = useTranslation(['tagsAlocacao', 'alocacao']);
  const paginationLabels = usePaginationLabels();
  const filterState = useAllocationTagFilters();
  const query = useAllocationTags(filterState.filters);
  const columnsState = useAllocationTagColumns();
  const exportTags = useExportAllocationTags(filterState.filters, columnsState.visibleColumns);
  /** `null` = nova tag; `undefined` = modal fechado. */
  const [editing, setEditing] = useState<AllocationTag | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<AllocationTag | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const { data, isLoading, isError } = query;
  const error = parseApiError(query.error);
  const isListLoading = isLoading || query.isPlaceholderData;

  if (isError && error && isSessionError(error)) return <NoIdentityState code={error.code} />;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.perPage)) : 1;
  // Sem filtro e sem nenhuma tag, o estado vazio substitui a tabela.
  const noTags = data && data.total === 0 && !filterState.hasActiveFilters;

  const newTagButton = (
    <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setEditing(null)}>
      {t('tagsAlocacao:nova')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('tagsAlocacao:titulo')}
        description={t('tagsAlocacao:descricao')}
        actions={newTagButton}
      />

      <section className="flex min-w-0 flex-col gap-4" aria-label={t('tagsAlocacao:titulo')}>
        <div className="flex flex-wrap items-center gap-2">
          {!isListLoading && data && (
            <span className="text-sm text-foreground-muted" aria-live="polite">
              {t('tagsAlocacao:total', { count: data.total, showing: data.items.length })}
            </span>
          )}
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<SlidersHorizontal size={14} />}
            onClick={() => setAdvancedOpen(true)}
          >
            {t('alocacao:filtros.avancados')}
          </Button>
          <ColumnsPicker
            columnsState={columnsState}
            labelKeys={ALLOCATION_TAG_COLUMN_LABEL_KEYS}
            namespace="tagsAlocacao"
          />
          <ExportButton
            exportMutation={exportTags}
            disabled={!data?.total}
            labels={{
              title: t('exportacao.titulo'),
              allPages: t('exportacao.todasPaginas', { count: data?.total ?? 0 }),
              currentPage: t('exportacao.paginaAtual', { count: data?.items.length ?? 0 }),
            }}
          />
        </div>

        <AllocationTagFilterChips filterState={filterState} />

        {isListLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {isError && (
          <Alert
            variant="danger"
            title={t((error ? i18nKeyForError(error.code) : 'alocacao:erros.unknown') as never)}
          />
        )}

        {!isListLoading && !isError && noTags && (
          <EmptyState
            title={t('tagsAlocacao:tabela.vazioTitulo')}
            description={t('tagsAlocacao:tabela.vazioDescricao')}
            action={newTagButton}
          />
        )}

        {!isListLoading && !isError && data && !noTags && (
          <div className="flex flex-col gap-4">
            <AllocationTagsTable
              items={data.items}
              filterState={filterState}
              columnsState={columnsState}
              onEdit={setEditing}
              onDelete={setDeleting}
            />
            <div className="flex flex-wrap items-center gap-4">
              <PerPageSelect value={filterState.perPage} onChange={filterState.setPerPage} />
              {totalPages > 1 && (
                <Pagination
                  {...paginationLabels}
                  page={filterState.filters.page}
                  totalPages={totalPages}
                  onPageChange={filterState.goToPage}
                />
              )}
            </div>
          </div>
        )}
      </section>

      <AllocationTagModal
        open={editing !== undefined}
        onClose={() => setEditing(undefined)}
        tag={editing ?? null}
      />
      <DeleteAllocationTagDialog tag={deleting} onClose={() => setDeleting(null)} />
      <AllocationTagFiltersDrawer
        open={advancedOpen}
        onClose={() => setAdvancedOpen(false)}
        filterState={filterState}
        isColumnVisible={columnsState.isVisible}
      />
    </div>
  );
}
